/**
 * Search over the Admin Panel. It knows nothing about pages or settings: it ranks a list of
 * entries (see adminIndex.js) against what was typed.
 *
 * An entry is { id, kind, title, subtitle, description, keywords, to, icon }. `kind` is one of
 * ENTRY_KINDS and decides which group the result is shown under.
 *
 * The box and pop-up that show the results are AdminSearchBox.js. (The two names differ by more than
 * case on purpose: on Windows `AdminSearch.js` and `adminSearch.js` are the same file.)
 */
export const ENTRY_KINDS = [
  { id: 'page', label: 'Pages' },
  { id: 'setting', label: 'Settings' },
  { id: 'action', label: 'Actions' },
  { id: 'person', label: 'People' }
];

const norm = (value) => String(value || '').toLowerCase();
const words = (value) => norm(value).split(/[^a-z0-9]+/).filter(Boolean);

/** What was typed, as lowercase words. */
export const queryTokens = (query) => norm(query).split(/[\s,]+/).filter(Boolean);

/**
 * Edit distance that counts a swap of two neighbouring letters as one slip ("stauts" for "status"), which is the
 * commonest typing error.
 */
export function editDistance(a, b) {
  const x = String(a);
  const y = String(b);
  if (x === y) return 0;
  const rows = x.length + 1;
  const cols = y.length + 1;
  const d = Array.from({ length: rows }, (_, i) => [i, ...new Array(cols - 1).fill(0)]);
  for (let j = 1; j < cols; j += 1) d[0][j] = j;
  for (let i = 1; i < rows; i += 1) {
    for (let j = 1; j < cols; j += 1) {
      const cost = x[i - 1] === y[j - 1] ? 0 : 1;
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost);
      if (i > 1 && j > 1 && x[i - 1] === y[j - 2] && x[i - 2] === y[j - 1]) d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1);
    }
  }
  return d[rows - 1][cols - 1];
}

/** How many slips a word of this length may have and still count as the one meant. Short words must be exact. */
const allowedSlips = (length) => (length <= 3 ? 0 : length <= 7 ? 1 : 2);

/** Does the typed word nearly match a word (or the start of a longer word, for a word still being typed)? */
const nearWord = (token, word) => {
  const slips = allowedSlips(token.length);
  if (!slips || !word) return false;
  if (Math.abs(word.length - token.length) <= slips && editDistance(token, word) <= slips) return true;
  return word.length > token.length && editDistance(token, word.slice(0, token.length)) <= slips;
};

export const scoreEntry = (entry, tokens, { fuzzy = false } = {}) => {
  const title = norm(entry.title);
  const titleWords = words(entry.title);
  const keywords = (entry.keywords || []).map(norm);
  const haystack = [title, norm(entry.subtitle), norm(entry.description), ...keywords].join(' ');
  let haystackWords = null;
  let score = 0;
  for (const token of tokens) {
    if (!haystack.includes(token)) {
      if (!fuzzy) return -1;
      haystackWords = haystackWords || Array.from(new Set(words(haystack)));
      // A near match is worth less than a real one, and a near match in the title a little more than elsewhere.
      if (titleWords.some((w) => nearWord(token, w))) score += 2;
      else if (haystackWords.some((w) => nearWord(token, w))) score += 1;
      else return -1;
      continue;
    }
    if (title === token) score += 12;
    else if (title.startsWith(token)) score += 8;
    else if (titleWords.some((w) => w.startsWith(token))) score += 6;
    else if (title.includes(token)) score += 4;
    else if (keywords.some((k) => k === token || words(k).some((w) => w.startsWith(token)))) score += 3;
    else score += 1;
  }
  return score;
};

/**
 * The entries every typed word appears in, best first within each kind. Words may match
 * anywhere in the title, the keywords or the description, so "break grace" finds a setting
 * called "Minutes after slot before break not started" when its keywords say grace.
 */
export function searchEntries(entries, query, { limitPerKind = 8, fuzzy = false } = {}) {
  const tokens = queryTokens(query);
  if (!tokens.length) return [];
  const byKind = new Map(ENTRY_KINDS.map((kind) => [kind.id, []]));
  (entries || []).forEach((entry) => {
    const score = scoreEntry(entry, tokens, { fuzzy });
    if (score < 0) return;
    (byKind.get(entry.kind) || byKind.get('page')).push({ entry, score });
  });
  const out = [];
  ENTRY_KINDS.forEach((kind) => {
    byKind.get(kind.id)
      .sort((a, b) => b.score - a.score || String(a.entry.title).localeCompare(String(b.entry.title)))
      .slice(0, limitPerKind)
      .forEach((hit) => out.push(hit.entry));
  });
  return out;
}

/**
 * The search the palette runs: exact first, and only when that finds nothing, again allowing a slip or two in
 * each word. `corrected` says the second kind was used, so the box can say "Showing close matches" instead of
 * quietly showing things that do not contain what was typed.
 */
export function searchWithTolerance(entries, query, options = {}) {
  const exact = searchEntries(entries, query, options);
  if (exact.length) return { results: exact, corrected: false };
  const close = searchEntries(entries, query, { ...options, fuzzy: true });
  return { results: close, corrected: close.length > 0 };
}

/** Splits text into pieces, marking those that match a typed word, so a result can show why it matched. */
export function highlightParts(text, tokens) {
  const source = String(text || '');
  if (!tokens.length || !source) return [{ text: source, hit: false }];
  const lower = source.toLowerCase();
  const marks = new Array(source.length).fill(false);
  tokens.forEach((token) => {
    let from = 0;
    for (;;) {
      const at = lower.indexOf(token, from);
      if (at < 0) break;
      for (let i = at; i < at + token.length; i += 1) marks[i] = true;
      from = at + token.length;
    }
  });
  const parts = [];
  let current = null;
  for (let i = 0; i < source.length; i += 1) {
    if (!current || current.hit !== marks[i]) {
      current = { text: '', hit: marks[i] };
      parts.push(current);
    }
    current.text += source[i];
  }
  return parts;
}
